import { Card, CardContent, CardHeader } from '#app/components/ui/card'
import { Skeleton } from '#app/components/ui/skeleton'

/**
 * Skeleton loading state that mimics the layout of IndividualReportView.
 * Shown while AI processing is in progress or when switching applicants.
 */
export function ReportSkeleton() {
	return (
		<div className="space-y-6 animate-in fade-in duration-300">
			{/* Score + Photo row */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				{/* Score card skeleton */}
				<Card className="rounded-[20px] border-none shadow-none">
					<CardContent className="flex flex-col items-center gap-4 p-6">
						<Skeleton className="h-32 w-32 rounded-full" />
						<Skeleton className="h-6 w-24" />
						<Skeleton className="h-4 w-36" />
					</CardContent>
				</Card>

				{/* Photo/ID card skeleton */}
				<Card className="rounded-[20px] border-none shadow-none">
					<CardContent className="flex flex-col items-center gap-4 p-6">
						<Skeleton className="h-32 w-32 rounded-lg" />
						<Skeleton className="h-5 w-28" />
						<Skeleton className="h-4 w-40" />
					</CardContent>
				</Card>
			</div>

			{/* Verification summary skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardHeader>
					<Skeleton className="h-5 w-48" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="flex items-center justify-between">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-6 w-20 rounded-full" />
						</div>
					))}
				</CardContent>
			</Card>

			{/* Rental preferences + Risk flags row */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<Card className="rounded-[20px] border-none shadow-none">
					<CardHeader>
						<Skeleton className="h-5 w-40" />
					</CardHeader>
					<CardContent className="space-y-3">
						{Array.from({ length: 4 }).map((_, i) => (
							<div key={i} className="flex items-center justify-between">
								<Skeleton className="h-4 w-28" />
								<Skeleton className="h-4 w-20" />
							</div>
						))}
					</CardContent>
				</Card>

				<Card className="rounded-[20px] border-none shadow-none">
					<CardHeader>
						<Skeleton className="h-5 w-24" />
					</CardHeader>
					<CardContent className="space-y-2">
						{Array.from({ length: 3 }).map((_, i) => (
							<div key={i} className="flex items-start gap-2">
								<Skeleton className="mt-1 h-3 w-3 rounded-full" />
								<Skeleton className="h-4 w-full" />
							</div>
						))}
					</CardContent>
				</Card>
			</div>

			{/* Personal details skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardHeader>
					<Skeleton className="h-5 w-36" />
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-2 gap-4">
						{Array.from({ length: 6 }).map((_, i) => (
							<div key={i} className="space-y-1">
								<Skeleton className="h-3 w-20" />
								<Skeleton className="h-4 w-32" />
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			{/* Monthly expenses skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardHeader>
					<Skeleton className="h-5 w-40" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="space-y-1">
							<div className="flex items-center justify-between">
								<Skeleton className="h-4 w-28" />
								<Skeleton className="h-4 w-16" />
							</div>
							<Skeleton className="h-2 w-full rounded-full" />
						</div>
					))}
				</CardContent>
			</Card>

			{/* Debt summary skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardHeader>
					<Skeleton className="h-5 w-32" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="flex items-center justify-between">
							<Skeleton className="h-4 w-36" />
							<Skeleton className="h-4 w-20" />
						</div>
					))}
				</CardContent>
			</Card>
		</div>
	)
}

/**
 * Compact skeleton for the combined/family report view
 */
export function CombinedReportSkeleton() {
	return (
		<div className="space-y-6 animate-in fade-in duration-300">
			{/* Group members skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardHeader>
					<Skeleton className="h-5 w-36" />
				</CardHeader>
				<CardContent className="space-y-3">
					{Array.from({ length: 2 }).map((_, i) => (
						<div key={i} className="flex items-center gap-3">
							<Skeleton className="h-10 w-10 rounded-full" />
							<div className="space-y-1">
								<Skeleton className="h-4 w-28" />
								<Skeleton className="h-3 w-20" />
							</div>
							<Skeleton className="ml-auto h-6 w-16 rounded-full" />
						</div>
					))}
				</CardContent>
			</Card>

			{/* Family score skeleton */}
			<Card className="rounded-[20px] border-none shadow-none">
				<CardContent className="flex flex-col items-center gap-4 p-6">
					<Skeleton className="h-24 w-24 rounded-full" />
					<Skeleton className="h-6 w-32" />
					<Skeleton className="h-4 w-48" />
				</CardContent>
			</Card>

			{/* Summary cards skeleton */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<Card key={i} className="rounded-[20px] border-none shadow-none">
						<CardHeader>
							<Skeleton className="h-5 w-32" />
						</CardHeader>
						<CardContent className="space-y-2">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-3/4" />
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	)
}
